import { Box, Grid, Stack } from "@mantine/core"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { DeliveryApi } from "../../apis"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { authDeliveryApprove } from "../../store/Auth"
import { TablePager } from "../delivery/atoms"
import { translateBackendError } from "../delivery/translate-error"
import { matchesBusinessName } from "@dehub/theme"
import { districtLabel } from "../../utils/district"
import { message } from "../../utils/message"
import { ListState } from "../ui"
import type { OrderTypeFilterValue } from "../active-deliveries/hooks"
import { useOrderTypeFilter } from "../active-deliveries/hooks"
import { AddToRouteDialog } from "./AddToRouteDialog"
import {
  ChannelFilterValue,
  useChannelFilter,
  useDeliveryStats,
  useDerivedPendingData,
  usePendingDeliveriesQuery,
} from "./hooks"
import { PendingDeliveriesActionBar } from "./PendingDeliveriesActionBar"
import { PendingDeliveriesTable } from "./PendingDeliveriesTable"
import { ProgressNoteModal } from "./ProgressNoteModal"
import { PendingDeliveryGroupCard } from "./PendingDeliveryGroupCard"
import { PendingFiltersToolbar, PendingQuickFilter } from "./PendingFiltersToolbar"
import { PendingSidePanel } from "./PendingSidePanel"
import { PendingStatsCards } from "./PendingStatsCards"
import { ConsolidationPreview } from "./ConsolidationPreview"

const ORDER_TYPE_VALUES: OrderTypeFilterValue[] = [
  null,
  "REGULAR",
  "CONSIGNMENT_INITIAL",
  "CONSIGNMENT_REPLENISHMENT",
  "CONSIGNMENT_RETURN",
  "CONSIGNMENT_TRANSFER",
]

const DEFAULT_PAGE_SIZE = 20
const UNKNOWN_DISTRICT = "-"

const groupByDistrictMap = (rows: IDeliveryNotePlus[]): Map<string, IDeliveryNotePlus[]> => {
  const buckets = new Map<string, IDeliveryNotePlus[]>()
  for (const dn of rows) {
    const district = districtLabel(dn.receiverBranch, dn.receiverWarehouse) ?? UNKNOWN_DISTRICT
    if (!buckets.has(district)) buckets.set(district, [])
    buckets.get(district)!.push(dn)
  }
  return buckets
}

/** Хүлээлтэнд tab (canvas Ord_DeliveryHub). */
export function PendingDeliveries() {
  const { t } = useTranslation("delivery")
  const channel = useChannelFilter()
  const orderTypeFilter = useOrderTypeFilter()
  const [page, setPage] = useState(1)
  const navigate = useNavigate()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [assignTargets, setAssignTargets] = useState<IDeliveryNotePlus[]>([])
  const [addToRouteOpen, setAddToRouteOpen] = useState(false)
  const [progressNoteDn, setProgressNoteDn] = useState<IDeliveryNotePlus | null>(null)
  const [groupByDistrict, setGroupByDistrict] = useState(false)
  const [quickFilter, setQuickFilter] = useState<PendingQuickFilter>("ALL")
  const [search, setSearch] = useState("")
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([])
  const [approving, setApproving] = useState(false)
  const dispatch = useDispatch()

  const { stats, refresh: refreshStats } = useDeliveryStats()
  const { data, isLoading, error, mutate } = usePendingDeliveriesQuery({
    channel: channel.value,
    page,
    pageSize,
  })

  const rows = useMemo(() => data?.rows ?? [], [data?.rows])
  const total = data?.count ?? 0

  const derived = useDerivedPendingData(rows)

  const availableDistricts = useMemo(() => {
    const set = new Set<string>()
    for (const dn of rows) {
      const d = districtLabel(dn.receiverBranch, dn.receiverWarehouse)
      if (d) set.add(d)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [rows])

  // Apply chip filter, search, then quick filter.
  const filteredRows = useMemo(() => {
    let r = orderTypeFilter.value ? rows.filter((dn) => (dn.orderType ?? "REGULAR") === orderTypeFilter.value) : rows

    if (dateRange[0] || dateRange[1]) {
      const start = dateRange[0] ? new Date(dateRange[0]).setHours(0, 0, 0, 0) : -Infinity
      const end = dateRange[1] ? new Date(dateRange[1]).setHours(23, 59, 59, 999) : Infinity
      r = r.filter((dn) => {
        if (!dn.createdAt) return false
        const time = new Date(dn.createdAt).getTime()
        return time >= start && time <= end
      })
    }

    if (selectedDistricts.length > 0) {
      r = r.filter((dn) => {
        const d = districtLabel(dn.receiverBranch, dn.receiverWarehouse)
        return d ? selectedDistricts.includes(d) : false
      })
    }

    if (search.trim()) {
      const needle = search.trim().toLowerCase()
      r = r.filter((dn) => {
        return (
          dn.refCode?.toLowerCase().includes(needle) ||
          dn.salesCode?.toLowerCase().includes(needle) ||
          // Both stored company spellings; never transliterated.
          matchesBusinessName(dn.receiverBusiness, search) ||
          dn.receiverBranch?.name?.toLowerCase().includes(needle) ||
          districtLabel(dn.receiverBranch, dn.receiverWarehouse)?.toLowerCase().includes(needle)
        )
      })
    }

    if (quickFilter !== "ALL") {
      r = r.filter((dn) => {
        const meta = derived.metaByDn.get(dn.id ?? "")
        if (!meta) return false
        switch (quickFilter) {
          case "VIP_URGENT":
            return meta.priority !== "NORMAL"
          case "SLA_RISK":
            return meta.slaSeverity === "CRITICAL"
          case "BIG_CARGO":
            return meta.totalSizeNumeric >= 10
          case "CUSTOMER_PICKUP":
            return dn.classification?.deliveryChannel === "CUSTOMER_PICKUP"
          default:
            return false
        }
      })
    }
    return r
  }, [rows, orderTypeFilter.value, dateRange, selectedDistricts, search, quickFilter, derived])

  const filterApplied =
    orderTypeFilter.value !== null ||
    channel.value !== null ||
    quickFilter !== "ALL" ||
    search.trim() !== "" ||
    dateRange[0] !== null ||
    dateRange[1] !== null ||
    selectedDistricts.length > 0

  const selectedRows = useMemo(() => rows.filter((r) => r.id && selectedIds.includes(r.id)), [rows, selectedIds])

  const onClearFilters = () => {
    orderTypeFilter.setValue(null)
    channel.setValue(null)
    setQuickFilter("ALL")
    setSearch("")
    setDateRange([null, null])
    setSelectedDistricts([])
    setPage(1)
  }

  const onToggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const onToggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.length === filteredRows.length ? [] : (filteredRows.map((r) => r.id ?? "").filter(Boolean) as string[]),
    )
  }

  const onAssignOne = (dn: IDeliveryNotePlus) => {
    setAssignTargets([dn])
    setAddToRouteOpen(true)
  }

  const onViewDetail = (dn: IDeliveryNotePlus) => {
    if (dn.id) navigate(`/supplier-delivery/detail/${dn.id}`)
  }

  // Backend rejects non-DRAFT/ASSIGNED DNs and DNs explicitly classified to a non-local
  // channel on POST /delivery-route, but ALLOWS unclassified DNs. Mirror that here.
  const isRoutable = (dn: IDeliveryNotePlus) => {
    if (dn.deliveryNoteStatus !== "DRAFT" && dn.deliveryNoteStatus !== "ASSIGNED") return false
    const ch = dn.classification?.deliveryChannel
    return ch == null || ch === "LOCAL_DELIVERY"
  }

  // «Шинэ маршрут үүсгэх» opens the route wizard pre-loaded with the routable DNs.
  const onCreateRouteFromRows = (targets: IDeliveryNotePlus[]) => {
    if (targets.length === 0) return
    const eligible = targets.filter(isRoutable)
    const ineligible = targets.length - eligible.length
    if (eligible.length === 0) {
      message.error(t("manage.pending.noneRoutable"))
      return
    }
    if (ineligible > 0) {
      message.error(t("manage.pending.someSkipped", { count: ineligible }))
    }
    navigate("/delivery-routes/new", {
      state: { preselectedDnIds: eligible.map((dn) => dn.id as string) },
    })
  }

  // O97/O100: the approve page is reached from here again. The BE reports `hasLoadableLines: false`
  // instead of failing, so say «nothing to load» rather than opening an empty page.
  const onApproveLoading = async () => {
    const ids = selectedRows.map((r) => r.id).filter(Boolean) as string[]
    if (ids.length === 0) return
    setApproving(true)
    try {
      const res = await DeliveryApi.approve(ids)
      if (res?.hasLoadableLines === false) {
        message.error("error.delivery_note.nothing_to_load")
        return
      }
      const empty = new Set<string>(res?.emptyDeliveryNoteIds ?? [])
      if (empty.size > 0) {
        const codes = selectedRows.filter((r) => r.id && empty.has(r.id)).map((r) => r.refCode)
        message.error(t("manage.approve.someEmpty", { codes: codes.join(", ") }))
      }
      dispatch(authDeliveryApprove(res))
      navigate("/supplier-delivery/approve")
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setApproving(false)
    }
  }

  const counts = {
    all: stats?.pendingByChannel.all ?? rows.length,
    local: stats?.pendingByChannel.local ?? 0,
    supply: stats?.pendingByChannel.supply ?? 0,
    pickup: stats?.pendingByChannel.pickup ?? 0,
  }

  const quickCounts = {
    all: rows.length,
    vipUrgent: derived.vipUrgentCount,
    slaRisk: derived.slaRiskCount,
    bigCargo: derived.bigFreightCount,
    customerPickup: derived.customerPickupCount,
  }

  const footer = (
    <TablePager page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
  )

  const tableProps = {
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
    onAssignOne,
    onProgressNote: setProgressNoteDn,
    onViewDetail,
    metaByDn: derived.metaByDn,
    countsByDistrict: derived.countsByDistrict,
  }

  const renderList = () => {
    if (isLoading) return <ListState state="loading" columns={6} />
    if (error)
      return <ListState state="error" onRetry={() => mutate()} errorCode={(error as { status?: number })?.status} />
    if (filteredRows.length === 0) {
      return filterApplied ? (
        <ListState state="noResults" action={{ onClick: onClearFilters }} />
      ) : (
        <ListState state="empty" title={t("manage.pending.emptyTitle")} description={t("manage.pending.emptyText")} />
      )
    }
    if (groupByDistrict) {
      return (
        <Stack gap="md">
          {Array.from(groupByDistrictMap(filteredRows).entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([district, groupRows]) => (
              <PendingDeliveryGroupCard
                key={district}
                district={district}
                rows={groupRows}
                {...tableProps}
                onCreateRouteForGroup={onCreateRouteFromRows}
                creatingRoute={false}
              />
            ))}
        </Stack>
      )
    }
    return <PendingDeliveriesTable rows={filteredRows} {...tableProps} footer={footer} />
  }

  return (
    <Stack gap="md">
      <PendingStatsCards
        slaRiskCount={derived.slaRiskCount}
        vipUrgentCount={derived.vipUrgentCount}
        totalPending={rows.length}
        channelBreakdown={{ local: counts.local, supply: counts.supply, pickup: counts.pickup }}
        avgWaitHours={derived.avgWaitHours}
      />

      {/* Spec §2.5 Tab 1 [1a] — creates DRAFT DNs from authorized orders, so refresh the list after. */}
      <ConsolidationPreview onConsolidated={() => mutate()} />

      <PendingFiltersToolbar
        channel={channel.value ?? "ALL"}
        onChannelChange={(val) => {
          channel.setValue(val === "ALL" ? null : (val as ChannelFilterValue))
          setPage(1)
        }}
        channelCounts={counts}
        quick={quickFilter}
        onQuickChange={setQuickFilter}
        counts={quickCounts}
        orderType={orderTypeFilter.value ?? "ALL"}
        onOrderTypeChange={(val) => {
          orderTypeFilter.setValue(val === "ALL" ? null : (val as OrderTypeFilterValue))
          setPage(1)
        }}
        orderTypeOptions={ORDER_TYPE_VALUES.map((v) => ({
          value: v ?? "ALL",
          label: v ? t(`badges.orderType.${v}`) : t("manage.filter.all"),
        }))}
        dateRange={dateRange}
        onDateRangeChange={(v) => {
          setDateRange(v)
          setPage(1)
        }}
        districtOptions={availableDistricts}
        selectedDistricts={selectedDistricts}
        onDistrictsChange={(v) => {
          setSelectedDistricts(v)
          setPage(1)
        }}
        groupByDistrict={groupByDistrict}
        onGroupByDistrictChange={setGroupByDistrict}
        search={search}
        onSearchChange={setSearch}
        onClear={onClearFilters}
      />

      <PendingDeliveriesActionBar
        selectedCount={selectedIds.length}
        selectedRows={selectedRows}
        onClear={() => setSelectedIds([])}
        onCreateRoute={() => onCreateRouteFromRows(selectedRows)}
        onAddToRoute={() => {
          setAssignTargets([])
          setAddToRouteOpen(true)
        }}
        onApproveLoading={onApproveLoading}
        approving={approving}
        creatingRoute={false}
      />

      <Grid gap="md">
        <Grid.Col span={{ base: 12, lg: 9 }}>
          <Box mih={200}>{renderList()}</Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 3 }}>
          <PendingSidePanel
            avgWaitHours={derived.avgWaitHours}
            slaRiskCount={derived.slaRiskCount}
            bigFreightCount={derived.bigFreightCount}
          />
        </Grid.Col>
      </Grid>

      <AddToRouteDialog
        opened={addToRouteOpen}
        onClose={() => {
          setAddToRouteOpen(false)
          setAssignTargets([])
        }}
        selectedDns={assignTargets.length > 0 ? assignTargets : selectedRows}
        onSuccess={() => {
          setSelectedIds([])
          setAssignTargets([])
          refreshStats()
          mutate()
        }}
      />

      <ProgressNoteModal dn={progressNoteDn} opened={!!progressNoteDn} onClose={() => setProgressNoteDn(null)} />
    </Stack>
  )
}
